import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useBrand } from "../BrandContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EMPTY = { logo_base64: "", accent_color: "#FFEB00", portal_title: "", login_tagline: "" };

/**
 * Reusable branding form.
 *
 * Props:
 *   - scope: "agency" or "client"
 *   - clientId: required when scope="client"
 *   - title: optional heading override
 */
export default function BrandingEditor({ scope, clientId, title }) {
  const { refresh: refreshBrand } = useBrand();
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const endpoint = scope === "client" ? `/branding/client/${clientId}` : `/branding/agency`;
  const heading = title || (scope === "client" ? "Client Branding Override" : "Agency Branding");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}${endpoint}`).then(r => {
      if (cancelled) return;
      const b = r.data?.branding || {};
      setDraft({ ...EMPTY, ...b });
    }).catch(() => {
      setDraft(EMPTY);
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [endpoint]);

  const onLogoPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_000_000) {
      setErr("Logo file too large. Use an image under 1MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setDraft(d => ({ ...d, logo_base64: reader.result }));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await axios.put(`${API}${endpoint}`, draft);
      setSavedAt(new Date());
      // Re-fetch the effective brand so the live UI updates.
      if (refreshBrand) refreshBrand();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save branding.");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm(`Reset ${scope === "client" ? "this client's branding" : "agency branding"} to defaults? This will remove the custom logo, color, title, and tagline.`)) return;
    setSaving(true); setErr("");
    try {
      await axios.put(`${API}${endpoint}`, EMPTY);
      setDraft(EMPTY);
      setSavedAt(new Date());
      if (refreshBrand) refreshBrand();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to reset.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-[#71717A]">Loading branding…</div>;

  return (
    <div className="card-flat p-6 space-y-5" data-testid={`branding-editor-${scope}`}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{heading}</h2>
          <p className="text-xs text-[#71717A] mt-1">
            {scope === "client"
              ? "Overrides the agency brand for this client's portal users. Leave blank to inherit."
              : "Default brand applied across the app. Per-client overrides take precedence for that client's portal users."}
          </p>
        </div>
        {savedAt && <span className="text-[11px] text-[#00C853]">Saved {savedAt.toLocaleTimeString()}</span>}
      </div>

      {/* Logo */}
      <div className="form-group">
        <label className="form-label">Logo</label>
        <div className="flex items-center gap-4">
          <div className="w-28 h-20 border border-[#2A2A2A] bg-[#0F0F0F] flex items-center justify-center overflow-hidden" data-testid="branding-logo-preview">
            {draft.logo_base64 ? (
              <img src={draft.logo_base64} alt="Logo preview" className="max-w-full max-h-full object-contain" />
            ) : (
              <span className="text-[10px] text-[#52525B] uppercase tracking-wider">No logo</span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={onLogoPick} className="hidden" data-testid="branding-logo-input" />
            <button type="button" onClick={() => fileRef.current?.click()} className="btn-secondary text-xs" data-testid="branding-logo-upload-btn">
              {draft.logo_base64 ? "Replace logo" : "Upload logo"}
            </button>
            {draft.logo_base64 && (
              <button type="button" onClick={() => setDraft(d => ({ ...d, logo_base64: "" }))} className="text-[11px] text-[#FF4444] hover:text-[#FF6666] text-left" data-testid="branding-logo-remove-btn">
                Remove logo
              </button>
            )}
            <p className="text-[10px] text-[#52525B]">PNG, JPG, WEBP or SVG · max ~1MB · displays at ~180×56px in sidebar</p>
          </div>
        </div>
      </div>

      {/* Accent color + Portal title */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-group">
          <label className="form-label">Accent color</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={draft.accent_color || "#FFEB00"}
              onChange={e => setDraft(d => ({ ...d, accent_color: e.target.value }))}
              className="w-12 h-10 border border-[#2A2A2A] bg-transparent cursor-pointer"
              data-testid="branding-accent-color-picker"
            />
            <input
              type="text"
              value={draft.accent_color || ""}
              onChange={e => setDraft(d => ({ ...d, accent_color: e.target.value }))}
              className="input-default flex-1"
              placeholder="#FFEB00"
              data-testid="branding-accent-color-text"
            />
          </div>
          <p className="text-[10px] text-[#52525B] mt-1">Primary accent — buttons, links, nav highlights.</p>
        </div>

        <div className="form-group">
          <label className="form-label">Portal title</label>
          <input
            type="text"
            value={draft.portal_title || ""}
            onChange={e => setDraft(d => ({ ...d, portal_title: e.target.value }))}
            className="input-default w-full"
            placeholder={scope === "client" ? "e.g. 5 Elements Performance Hub" : "Digital Impressions"}
            data-testid="branding-portal-title-input"
          />
          <p className="text-[10px] text-[#52525B] mt-1">Browser tab title. {scope === "client" && "Defaults to the client name if blank."}</p>
        </div>
      </div>

      {/* Login tagline (agency only — login page is shared) */}
      {scope === "agency" && (
        <div className="form-group">
          <label className="form-label">Login page tagline <span className="text-[#71717A] text-xs">(optional)</span></label>
          <input
            type="text"
            value={draft.login_tagline || ""}
            onChange={e => setDraft(d => ({ ...d, login_tagline: e.target.value }))}
            className="input-default w-full"
            placeholder="e.g. Performance Intelligence OS"
            data-testid="branding-login-tagline-input"
          />
        </div>
      )}

      {err && <p className="text-sm text-[#FF4444]" data-testid="branding-error">{err}</p>}

      <div className="flex items-center gap-2 pt-2">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="branding-save-btn">
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button onClick={clear} disabled={saving} className="btn-secondary text-[#FF4444]" data-testid="branding-reset-btn">
          Reset to defaults
        </button>
      </div>
    </div>
  );
}
